import React from "react"
import { Tabs } from "antd"
import { DndContext, PointerSensor, useSensor } from "@dnd-kit/core"
import { SortableContext, useSortable, horizontalListSortingStrategy, arrayMove } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import PropTypes from "prop-types"
import "./DraggableTabs.scss"

const DraggableTabNode = ({ className, ...props }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: props["data-node-key"],
  })
  const style = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    cursor: "move",
  }
  return React.cloneElement(props.children, {
    ref: setNodeRef,
    style,
    ...attributes,
    ...listeners,
  })
}

const DraggableTabs = ({ items: itemsProp = [], activeKey, onDragEnd, onChange, onEdit }) => {
  const sensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 10,
    },
  })

  // Prevent user from removing tabs, by setting the closable items prop to false for all tabs
  const items = itemsProp.map((tab) => {
    const newTab = { ...tab }
    newTab.closable = false
    return newTab
  })

  const handleDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      const activeIndex = items.findIndex((i) => i.key === active.id)
      const overIndex = items.findIndex((i) => i.key === over?.id)
      const newOrder = arrayMove(items, activeIndex, overIndex)
      onDragEnd(newOrder)
    }
  }

  return (
    <Tabs
      type="editable-card"
      items={items}
      activeKey={activeKey}
      onChange={(activeKey) => {
        if (onChange) onChange(activeKey)
      }}
      onEdit={onEdit}
      renderTabBar={(tabBarProps, DefaultTabBar) => (
        <DndContext
          sensors={[sensor]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map((i) => i.key)}
            strategy={horizontalListSortingStrategy}
          >
            <DefaultTabBar {...tabBarProps}>
              {(node) => (
                <DraggableTabNode
                  {...node.props}
                  key={node.key}
                >
                  {node}
                </DraggableTabNode>
              )}
            </DefaultTabBar>
          </SortableContext>
        </DndContext>
      )}
      className="draggable-tabs"
    />
  )
}

DraggableTabs.propTypes = {
  /**
   * Configure tab content with an array of [TabItemType](https://ant.design/components/tabs#tabitemtype).
   */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      icon: PropTypes.node,
      children: PropTypes.node,
    })
  ).isRequired,
  /**
   * Current tab pane's key.
   */
  activeKey: PropTypes.string.isRequired,
  /**
   * Callback executed when the tabs are re-ordered. It receives an array of [TabItemType](https://ant.design/components/tabs#tabitemtype).
   *
   * `(items: TabItemType[]) => void`
   */
  onDragEnd: PropTypes.func.isRequired,
  /**
   * Callback executed when active tab is changed.
   *
   * `(activeKey: string) => void`
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Callback executed when "+" button is clicked.
   *
   * `(activeKey: string) => void`
   */
  onEdit: PropTypes.func,
}

export default DraggableTabs
